import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Send, Search, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading, EmptyState, PageHeader, fmtDateTime } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge, Thread, AttachUploader, catKey, priKey } from "@/pages/shared/SupportPage";

const STATUSES = ["OPEN", "IN_PROGRESS", "WAITING", "RESOLVED", "CLOSED"];
const stKey = { OPEN: "stOpen", IN_PROGRESS: "stInProgress", WAITING: "stWaiting", RESOLVED: "stResolved", CLOSED: "stClosed" };
const needsResp = (tk) => ["OPEN", "WAITING"].includes(tk.status) || tk.last_reply_type === "user";
const waitMs = (tk) => Date.now() - new Date(tk.last_reply_type === "user" ? tk.updated_at : tk.created_at).getTime();
const slaLabel = (ms) => { const h = Math.floor(ms / 3.6e6); return h < 1 ? "<1h" : h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`; };
const slaColor = (ms) => ms > 2.592e8 ? "bg-red-100 text-red-700" : ms > 8.64e7 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";

export default function AdminSupport() {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [replyAtts, setReplyAtts] = useState([]);
  const [note, setNote] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (status !== "ALL") params.set("status", status);
    if (q) params.set("q", q);
    return api.get(`/admin/support/tickets?${params.toString()}`).then((r) => setRows(r.data.tickets));
  };
  useEffect(() => { const timer = setTimeout(load, 200); return () => clearTimeout(timer); }, [status, q]);
  const openTicket = (id) => api.get(`/admin/support/tickets/${id}`).then((r) => setActive(r.data.ticket));

  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/admin/support/tickets/${active.id}/reply`, { message: reply, attachments: replyAtts }); setReply(""); setReplyAtts([]); toast.success(t("sup.replySent")); openTicket(active.id); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/support/tickets/${active.id}/note`, { body: note }); setNote(""); toast.success(t("sup.noteSaved")); openTicket(active.id); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeStatus = async (v) => {
    try { await api.post(`/admin/support/tickets/${active.id}/status`, { status: v }); toast.success(t("sup.statusUpdated")); openTicket(active.id); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (rows === null) return <PageLoading />;

  if (active) {
    return (
      <div data-testid="admin-support-detail">
        <button onClick={() => setActive(null)} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="admin-support-back"><ArrowLeft className="h-4 w-4" /> {t("sup.back")}</button>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
            <div className="mb-4 border-b border-slate-100 pb-4">
              <div className="font-heading text-lg font-bold text-navy">{active.subject}</div>
              <div className="mt-1 text-xs text-slate-400">{t(`sup.${catKey[active.category]}`)} · {t(`sup.${priKey[active.priority]}`)}</div>
            </div>
            <Thread messages={active.messages} ticketId={active.id} />
            <div className="mt-5 space-y-2">
              <div className="flex items-end gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder={t("sup.adminReplyPlaceholder")} data-testid="admin-support-reply-input" />
                <Button onClick={sendReply} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="admin-support-reply-send"><Send className="h-4 w-4" /></Button>
              </div>
              <AttachUploader value={replyAtts} onChange={setReplyAtts} testid="admin-support-attach" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("sup.changeStatus")}</div>
              <Select value={active.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="admin-support-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`sup.${stKey[s]}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("sup.requester")}</div>
              <div className="font-semibold text-navy">{active.creator_name}</div>
              <div className="text-slate-500">{active.creator_email}</div>
              <div className="mt-1 text-slate-400">{active.company_name || "—"}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">{(active.creator_role || "").replace("_", " ")}</div>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="admin-internal-notes">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-700">{t("sup.internalNotes")}</div>
              <div className="max-h-48 space-y-2 overflow-auto">
                {(active.internal_notes || []).length === 0 ? <div className="text-xs text-slate-400">{t("sup.noNotes")}</div> :
                  active.internal_notes.map((n) => (
                    <div key={n.id} className="rounded-lg bg-white p-2 text-xs">
                      <div className="whitespace-pre-wrap text-slate-700">{n.body}</div>
                      <div className="mt-1 text-[10px] text-slate-400">{n.author_name} · {fmtDateTime(n.created_at)}</div>
                    </div>
                  ))}
              </div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t("sup.notePlaceholder")} className="mt-2 bg-white" data-testid="admin-note-input" />
              <Button onClick={addNote} size="sm" className="mt-2 w-full rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="admin-note-save">{t("sup.saveNote")}</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-support">
      <PageHeader title={t("sup.aTitle")} subtitle={t("sup.aSubtitle")} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("sup.searchPlaceholder")} className="pl-9" data-testid="admin-support-search" /></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="admin-support-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("sup.allStatuses")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`sup.${stKey[s]}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={LifeBuoy} title={t("sup.aNoTickets")} description="" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-support-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("sup.subject")}</th><th className="px-4 py-3">{t("sup.company")}</th><th className="px-4 py-3">{t("sup.from")}</th><th className="px-4 py-3">{t("sup.priority")}</th><th className="px-4 py-3">{t("sup.waiting")}</th><th className="px-4 py-3">{t("sup.status")}</th><th className="px-4 py-3">{t("sup.updated")}</th></tr>
            </thead>
            <tbody>
              {[...rows].sort((a, b) => { const na = needsResp(a), nb = needsResp(b); if (na !== nb) return na ? -1 : 1; if (na) return waitMs(b) - waitMs(a); return new Date(b.updated_at) - new Date(a.updated_at); }).map((tk) => (
                <tr key={tk.id} onClick={() => openTicket(tk.id)} data-testid={`admin-support-row-${tk.id}`} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-navy">{tk.subject}</td>
                  <td className="px-4 py-3 text-slate-500">{tk.company_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{tk.creator_name}</td>
                  <td className="px-4 py-3 text-slate-500">{t(`sup.${priKey[tk.priority]}`)}</td>
                  <td className="px-4 py-3">{needsResp(tk) && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${slaColor(waitMs(tk))}`} data-testid={`sla-${tk.id}`}>{slaLabel(waitMs(tk))}</span>}</td>
                  <td className="px-4 py-3"><StatusBadge status={tk.status} /></td>
                  <td className="px-4 py-3 text-slate-400">{fmtDateTime(tk.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
